import { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import api from '../../services/api';
import { clientAuthConfig } from '../../utils/clientAuthConfig';
import { getStoredConversationId, setStoredConversationId } from './convStorage';
import {
  listingStatusLabel,
  consultationStatusLabel,
} from './sellerPortalLabels';

export function useSellerPortalData(slug, client, reload) {
  const [consultations, setConsultations] = useState([]);
  const [consultationsError, setConsultationsError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [profile, setProfile] = useState({
    property_address: '',
    property_city: '',
    timeline: '',
    notes: '',
  });
  const profileDirtyRef = useRef(false);

  const authCfg = useCallback(() => clientAuthConfig(slug), [slug]);

  const resolveLinkedConversationId = useCallback(async () => {
    const localId = slug ? getStoredConversationId(slug, 'seller') : null;
    if (localId) {
      try {
        await api.post('/clients/link-conversation', { conversation_id: localId }, authCfg());
      } catch {
        /* still use local chat id */
      }
      return localId;
    }
    try {
      const latest = await api.get('/clients/me/conversations/latest', authCfg());
      const serverId = latest.data?.conversation?.conversation_id || null;
      if (serverId) {
        setStoredConversationId(slug, serverId, 'seller');
        return serverId;
      }
    } catch {
      /* no linked chat */
    }
    return null;
  }, [slug, authCfg]);

  const loadConsultations = useCallback(async () => {
    try {
      const res = await api.get('/clients/me/consultations', authCfg());
      setConsultations(res.data.consultations || []);
      setConsultationsError(false);
    } catch {
      setConsultationsError(true);
    }
  }, [authCfg]);

  const loadConsultationsRef = useRef(loadConsultations);
  loadConsultationsRef.current = loadConsultations;

  // Boot once per slug + agent_client_id — not on every client object refresh from reload().
  // Do not auto sync-from-chat (a stale stored conversation can overwrite the property on file).
  useEffect(() => {
    if (!client?.agent_client_id || !slug) return;
    loadConsultationsRef.current();
  }, [client?.agent_client_id, slug]);

  const handleSyncFromChat = async () => {
    const assistantName = client?.agent?.assistant_name || 'Ava';
    const conversationId = await resolveLinkedConversationId();
    if (!conversationId) {
      setSyncMsg(`No linked chat found — talk with ${assistantName} first, then try again.`);
      return;
    }
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await api.post(
        '/clients/sync-from-chat',
        { conversation_id: conversationId },
        authCfg()
      );
      await reload();
      await loadConsultations();
      const synced = res.data?.seller_synced;
      if (synced?.property_address || synced?.property_city || synced?.timeline) {
        const place = [synced.property_address, synced.property_city].filter(Boolean).join(', ');
        setSyncMsg(
          place
            ? `Updated property details (${place}) from your latest chat with ${assistantName}.`
            : `Updated from your latest chat with ${assistantName}.`
        );
      } else {
        setSyncMsg(
          `No new property details in chat yet — keep telling ${assistantName} about your home, then refresh again.`
        );
      }
    } catch (err) {
      const apiErr = err.response?.data?.error;
      if (err.response?.status === 401) {
        setSyncMsg('Session expired — please sign out and sign in again, then retry.');
      } else if (apiErr) {
        setSyncMsg(apiErr);
      } else if (!err.response) {
        setSyncMsg('Cannot reach server — make sure the backend is running, then try again.');
      } else {
        setSyncMsg('Could not sync from chat.');
      }
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!client?.seller_profile || profileDirtyRef.current) return;
    const sp = client.seller_profile;
    setProfile({
      property_address: sp.property_address || '',
      property_city: sp.property_city || '',
      timeline: sp.timeline || '',
      notes: sp.notes || '',
    });
  }, [client?.agent_client_id, client?.seller_profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    profileDirtyRef.current = true;
    setProfile((p) => ({ ...p, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      await api.patch('/clients/me/seller-profile', {
        property_address: profile.property_address || null,
        property_city: profile.property_city || null,
        timeline: profile.timeline || null,
        notes: profile.notes || null,
      }, authCfg());
      profileDirtyRef.current = false;
      await reload();
      setSaveMsg('Saved.');
    } catch {
      setSaveMsg('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return {
    consultations,
    syncing,
    syncMsg,
    handleSyncFromChat,
    profile,
    saving,
    saveMsg,
    handleChange,
    handleSave,
    consultationsError,
  };
}

export function SellerPortalPanels({
  client,
  slug,
  consultations,
  syncing,
  syncMsg,
  onSyncFromChat,
  profile,
  saving,
  saveMsg,
  onChange,
  onSave,
  consultationsError,
}) {
  const agentFirst = client.agent?.first_name || 'your agent';
  const aiName = client.agent?.assistant_name || 'Ava';
  const sp = client.seller_profile;

  return (
    <>
      <div className="client-portal-unified__seller-grid">
        <section className="client-portal__panel client-portal__panel--seller">
          <h2>
            <MapPin size={18} /> Property
          </h2>
          {!sp?.property_address && !sp?.property_city && (
            <p className="client-portal__muted">
              No property on file yet. Chat with {aiName} about the home you are considering
              selling, or fill it in below — you can edit it anytime.
            </p>
          )}
          <p className="client-portal__detail">
            <strong>Listing status:</strong>{' '}
            {listingStatusLabel(sp?.listing_status, aiName)}
          </p>
          <form className="client-form" onSubmit={onSave}>
            <label>
              Address
              <input
                name="property_address"
                type="text"
                value={profile.property_address}
                onChange={onChange}
                placeholder="123 Main St"
              />
            </label>
            <label>
              City
              <input
                name="property_city"
                type="text"
                value={profile.property_city}
                onChange={onChange}
                placeholder="St. John's"
              />
            </label>
            <label>
              Timeline
              <input
                name="timeline"
                type="text"
                value={profile.timeline}
                onChange={onChange}
                placeholder="e.g. 3–6 months"
              />
            </label>
            <label>
              Notes
              <textarea
                name="notes"
                rows={3}
                value={profile.notes}
                onChange={onChange}
                placeholder="Anything else worth flagging about your home or timeline"
              />
            </label>
            {saveMsg && (
              <p className={saveMsg === 'Saved.' ? 'client-form__success' : 'client-form__error'}>
                {saveMsg}
              </p>
            )}
            <div className="client-portal__actions">
              <button type="submit" className="client-form__submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save property details'}
              </button>
            </div>
          </form>
          <SellerHomeRamble slug={slug} agentFirst={agentFirst} />
        </section>

        <section className="client-portal__panel">
          <h2>
            <Calendar size={18} /> Consultation with {agentFirst}
          </h2>
          {consultationsError ? (
            <p className="client-form__error">Couldn&apos;t load consultations. Refresh the page to try again.</p>
          ) : consultations.length === 0 ? (
            <p className="client-portal__muted">
              No consultation on file yet. Ask {aiName} for a market consultation on your home.
            </p>
          ) : (
            <ul className="client-showing-list">
              {consultations.map((consult) => (
                <li key={consult.id} className="client-showing-card">
                  <p className="client-portal__detail">
                    <strong>Status:</strong> {consultationStatusLabel(consult.status)}
                  </p>
                  {consult.scheduled_at && (
                    <p className="client-portal__detail">
                      <strong>When:</strong>{' '}
                      {new Date(consult.scheduled_at).toLocaleString()}
                    </p>
                  )}
                  {consult.notes && (
                    <p className="client-portal__detail client-portal__notes">{consult.notes}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <p className="client-portal__sync-hint">
        Pull the latest property details from your chat.{' '}
        <button
          type="button"
          className="client-portal__sync-link"
          onClick={onSyncFromChat}
          disabled={syncing}
        >
          {syncing ? 'syncing…' : 'Refresh selling details from chat'}
        </button>
      </p>
      {syncMsg && <p className="client-portal__save-msg">{syncMsg}</p>}

      <section className="client-portal__panel client-portal__panel--muted">
        <h2>More coming</h2>
        <ul className="client-portal__soon">
          <li>Document vault (listing agreements)</li>
          <li>Viewing calendar & buyer feedback</li>
          <li>Offer status tracking</li>
        </ul>
      </section>
    </>
  );
}

function SellerHomeRamble({ slug, agentFirst }) {
  const [transcript, setTranscript] = useState('');
  const [shareOk, setShareOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (transcript.trim().length < 20) {
      setMsg('Say a bit more about the home — a short ramble is fine.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/listing-files/seller/ramble', {
        transcript: transcript.trim(),
        seller_share_ok: shareOk,
      }, clientAuthConfig(slug));
      setTranscript('');
      setMsg(shareOk
        ? `${agentFirst} will review what you said before buyers hear any of it.`
        : 'Saved for your agent. Buyers will not hear this unless you also agree to share and they approve the lines.');
    } catch (err) {
      setMsg(err?.response?.data?.error || 'Could not save what you said about the home.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="client-form" onSubmit={handleSubmit} style={{ marginTop: 20 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Tell {agentFirst} about the home</h3>
      <p className="client-portal__muted">
        What Realtor.ca will not say — roof, tank, heat, quirks, what you love living with.
        Skip schools, crime, and who lives nearby.
      </p>
      <label>
        About the house
        <textarea
          rows={4}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="The roof was done in 2021, the oil tank is new…"
        />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          checked={shareOk}
          onChange={(e) => setShareOk(e.target.checked)}
        />
        {agentFirst} may use approved lines with buyers
      </label>
      {msg && <p className="client-portal__save-msg">{msg}</p>}
      <div className="client-portal__actions">
        <button type="submit" className="client-form__submit" disabled={busy}>
          {busy ? 'Saving...' : 'Save what I said'}
        </button>
      </div>
    </form>
  );
}
